import React, { useState, useEffect } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Autocomplete } from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import BootstrapTextField from "Common/BootstrapTextField";
import { City, Country, State } from "country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { useDispatch } from "react-redux";
import {
  addOrganisation,
  fetchOrganisationById,
  updateOrganisation,
} from "slices/UserManagement/organisation/thunk";
import { autocompleteDropdownStyles } from "Constants/Style";
import LoadingSpinner from "components/LoadingSpinner";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { fetchAddressFromPincode } from "helpers/geonamesHelper";

const AddOrganisation = () => {
  const { id } = useParams<{ id: string }>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);
  const [toastState, setToastState] = useState(true);
  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [formData, setFormData] = useState({
    organization_name: "",
    industry_type: "",
    org_address: "",
    org_country: "",
    org_state: "",
    org_city: "",
    org_pincode: "",
  });
  const dispatch = useDispatch<any>();

  const commonInputStyle = {
    fontSize: "0.8rem",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
  };
  const commonLabelStyle = {
    fontSize: "0.8rem",
    marginBottom: 6,
    marginTop: 12,
  };

  useScrollToFormikError(errors as any, submitCount, isSubmitting);

  const handlePincodeAutofill = async (pinVal?: string) => {
    const pin = (pinVal ?? formData.org_pincode)?.toString().trim();
    if (!pin || pin.length < 6) return;

    const selectedCountry = countries.find(
      (c: any) =>
        c.name?.toLowerCase() === formData.org_country?.toLowerCase(),
    );
    const countryCode = selectedCountry?.isoCode;

    try {
      const data = await fetchAddressFromPincode(pin, countryCode);
      if (!data) return;

      const resolvedCountry =
        countries.find((c: any) => c.isoCode === data.countryCode) ||
        Country.getAllCountries().find(
          (c: any) => c.isoCode === data.countryCode,
        );

      if (!resolvedCountry) return;

      const countryStates = State.getStatesOfCountry(resolvedCountry.isoCode);
      setStates(countryStates);

      const resolvedState = countryStates.find(
        (s: any) =>
          s.name?.toLowerCase() === data.adminName1?.toLowerCase() ||
          s.isoCode?.toLowerCase() === data.adminCode1?.toLowerCase(),
      );

      if (resolvedState) {
        const stateCities = City.getCitiesOfState(
          resolvedCountry.isoCode,
          resolvedState.isoCode,
        );
        setCities(stateCities);
      } else {
        setCities([]);
      }

      setFormData((prev) => ({
        ...prev,
        org_country: resolvedCountry.name,
        org_state: resolvedState
          ? resolvedState.name
          : data.adminName1 || "",
        org_city: data.placeName || "",
      }));
      setErrors((prev) => ({
        ...prev,
        org_country: "",
        org_state: "",
        org_city: "",
        org_pincode: "",
      }));
    } catch (err) {
      console.error("Failed to fetch address details for pincode", err);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    const nextValue =
      name === "org_pincode" ? value.replace(/\D/g, "").slice(0, 6) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "org_pincode" && nextValue.length === 6) {
      handlePincodeAutofill(nextValue);
    }
  };

  const requiredLabel = (label: string) => (
    <>
      {label} <span style={{ color: "red" }}>*</span>
    </>
  );

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.organization_name.trim())
      newErrors.organization_name = "Organisation Name is required";
    if (!formData.industry_type.trim())
      newErrors.industry_type = "Industry is required";
    if (!formData.org_address.trim())
      newErrors.org_address = "Address is required";
    if (formData.org_country.trim() === "")
      newErrors.org_country = "Country is required";
    if (states.length > 0 && !formData.org_state.trim()) {
      newErrors.org_state = "State is required";
    }
    if (cities.length > 0 && !formData.org_city.trim()) {
      newErrors.org_city = "City is required";
    }

    if (!formData?.org_pincode || !formData.org_pincode.toString().trim()) {
      newErrors.org_pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(formData.org_pincode.toString().trim())) {
      newErrors.org_pincode = "Pincode must 6 digits";
    }
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitCount((prev) => prev + 1);
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      // if (toastState)
      //   toast.error("Please enter all required fields correctly.");
      setErrors(validationErrors);
      setToastState(false);
      return;
    }
    setIsSubmitting(true);
    try {
      let response;

      const payload = {
        organization_name: formData.organization_name?.toString().trim() || "",
        industry_type: formData.industry_type?.toString().trim() || "",
        org_address: formData.org_address?.toString().trim() || "",
        org_country: formData.org_country?.toString().trim() || "",
        org_state: formData.org_state?.toString().trim() || "",
        org_city: formData.org_city?.toString().trim() || "",
        org_pincode: formData.org_pincode?.toString().trim() || "",
      };

      if (id) {
        response = await dispatch(
          updateOrganisation({
            id,
            orgData: payload,
          }),
        ).unwrap();
      } else {
        response = await dispatch(addOrganisation(payload)).unwrap();
        clearTableState(TableKeys?.USER_MANAGEMENT?.ORGANISATION);
      }

      toast.success(`Organisation ${id ? "updated" : "created"} successfully`);
      navigate("/organisations-list");
    } catch (error: any) {
      toast.error(error || "Failed to save organisation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelButton = () => {
    setFormData({
      organization_name: "",
      industry_type: "",
      org_address: "",
      org_country: "",
      org_state: "",
      org_city: "",
      org_pincode: "",
    });
    setErrors({});
    navigate("/organisations-list");
  };

  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const loadOrganization = async () => {
      try {
        const res = await dispatch(fetchOrganisationById(id)).unwrap();
        const response = res?.data?.data;
        const allCountries = Country.getAllCountries();
        const selectedCountry = allCountries.find(
          (c) => c.name?.toLowerCase() === response.org_country?.toLowerCase(),
        );

        const countryIsoCode = selectedCountry?.isoCode || "";
        const statesOfCountry = countryIsoCode
          ? State.getStatesOfCountry(countryIsoCode)
          : [];
        const selectedState = statesOfCountry.find(
          (s) => s.name?.toLowerCase() === response.org_state?.toLowerCase(),
        );
        const stateIsoCode = selectedState?.isoCode || "";

        const citiesOfState =
          countryIsoCode && stateIsoCode
            ? City.getCitiesOfState(countryIsoCode, stateIsoCode)
            : [];

        setCountries(allCountries);
        setStates(statesOfCountry);
        setCities(citiesOfState);

        setFormData({
          organization_name: response.organization_name || "",
          industry_type: response.industry_type || "",
          org_address: response.org_address || "",
          org_country: response.org_country || "",
          org_state: response.org_state || "",
          org_city: response.org_city || "",
          org_pincode: response.org_pincode || "",
        });
      } catch (error: any) {
        toast.error(error || "Failed to fetch organisation details");
      } finally {
        setLoading(false);
      }
    };

    loadOrganization();
  }, [id, dispatch]);

  // useEffect(() => {
  //   console.log("formData", formData);
  //   console.log("states", states);
  //   console.log("cities", cities);
  // });

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Organisation" : "Add Organisation"}
        titleLink="/organisations-list"
        breadcrumbs={[
          { label: "Organisation", to: "/organisations-list" },
          { label: id ? "Edit Organisation" : "Add Organisation", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                {requiredLabel("Organisation Name")}
              </Form.Label>
              <Form.Control
                id="organization_name"
                name="organization_name"
                value={formData.organization_name}
                onChange={handleChange}
                isInvalid={!!errors.organization_name}
                placeholder="Enter Organisation Name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.organization_name}
              </Form.Control.Feedback>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                {requiredLabel("Industry Type")}
              </Form.Label>
              <Form.Control
                id="industry_type"
                name="industry_type"
                value={formData.industry_type}
                onChange={handleChange}
                isInvalid={!!errors.industry_type}
                placeholder="Enter Industry Type"
              />
              <Form.Control.Feedback type="invalid">
                {errors.industry_type}
              </Form.Control.Feedback>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                {requiredLabel("Address")}
              </Form.Label>
              <Form.Control
                id="org_address"
                name="org_address"
                value={formData.org_address}
                onChange={handleChange}
                isInvalid={!!errors.org_address}
                placeholder="Enter Address"
              />
              <Form.Control.Feedback type="invalid">
                {errors.org_address}
              </Form.Control.Feedback>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                Pincode <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                id="org_pincode"
                name="org_pincode"
                placeholder="Enter Pincode"
                value={formData.org_pincode}
                onChange={handleChange}
                onBlur={() => handlePincodeAutofill()}
                isInvalid={!!errors.org_pincode}
                maxLength={6}
              />
              <Form.Control.Feedback type="invalid">
                {errors.org_pincode}
              </Form.Control.Feedback>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md={4} xs={12} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Country <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                disablePortal
                options={countries}
                getOptionLabel={(option) => option.name}
                value={
                  countries.find(
                    (c: any) =>
                      c.name?.toLowerCase() ===
                      formData.org_country?.toLowerCase(),
                  ) || null
                }
                onChange={(e, selectedCountry) => {
                  setFormData((prev) => ({
                    ...prev,
                    org_country: selectedCountry?.name || "",
                    org_state: "",
                    org_city: "",
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    org_country: "",
                    org_state: "",
                    org_city: "",
                  }));

                  if (selectedCountry) {
                    setIsLoadingStates(true);
                    const newStates = State.getStatesOfCountry(
                      selectedCountry.isoCode,
                    );
                    setStates(newStates);
                    setCities([]);
                    setIsLoadingStates(false);
                  } else {
                    setStates([]);
                    setCities([]);
                  }
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name,
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Country"
                    id="org_country"
                    name="org_country"
                    size="small"
                    error={!!errors.org_country}
                    helperText={errors.org_country}
                    fullWidth
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                  />
                )}
              />
            </Col>

            <Col md={4} xs={12} className="mb-3">
              {(() => {
                const selectedCountry = countries.find(
                  (c: any) =>
                    c.name?.toLowerCase() ===
                    formData.org_country?.toLowerCase(),
                );

                // console.log("selectedCountry", selectedCountry);
                const hasStates = states.length > 0;

                return (
                  <>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      {hasStates ? "State" : "State / Region"}{" "}
                      {hasStates && <span className="text-danger">*</span>}
                    </Form.Label>

                    {hasStates ? (
                      <Autocomplete
                        disablePortal
                        options={states}
                        getOptionLabel={(option) => option.name}
                        loading={isLoadingStates}
                        value={
                          states.find(
                            (s: any) =>
                              s.name?.toLowerCase() ===
                              formData.org_state?.toLowerCase(),
                          ) || null
                        }
                        onChange={(e, selectedState) => {
                          const selectedCountry = countries.find(
                            (c: any) => c.name === formData.org_country,
                          );

                          setFormData((prev) => ({
                            ...prev,
                            org_state: selectedState?.name || "",
                            org_city: "",
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            org_state: "",
                            org_city: "",
                          }));

                          if (selectedCountry && selectedState) {
                            setIsLoadingCities(true);
                            const newCities = City.getCitiesOfState(
                              selectedCountry.isoCode,
                              selectedState.isoCode,
                            );
                            setCities(newCities);
                            setIsLoadingCities(false);
                          } else {
                            setCities([]);
                          }
                        }}
                        ListboxProps={autocompleteDropdownStyles.ListboxProps}
                        renderOption={(props, option) =>
                          autocompleteDropdownStyles.renderOption(
                            props,
                            option,
                            (opt) => opt.name,
                          )
                        }
                        renderInput={(params) => (
                          <BootstrapTextField
                            {...params}
                            placeholder="Select State"
                            id="org_state"
                            name="org_state"
                            size="small"
                            error={!!errors.org_state}
                            helperText={errors.org_state}
                            fullWidth
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                            }}
                          />
                        )}
                        disabled={!formData.org_country}
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        id={`org_state`}
                        name={`org_state`}
                        placeholder="Enter State / Region"
                        value={formData.org_state}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            org_state: e.target.value,
                            org_city: "",
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            org_state: "",
                            org_city: "",
                          }));
                        }}
                        style={commonInputStyle}
                        disabled={!formData.org_country}
                      />
                    )}
                  </>
                );
              })()}
            </Col>

            <Col md={4} xs={12} className="mb-3">
              {(() => {
                const hasStates = states.length > 0;
                const hasCities = cities.length > 0;

                // City logic:
                // 1. If Country has NO States -> Input field
                // 2. If Country has States:
                //    a. If State is selected:
                //       i. If State has Cities -> Dropdown
                //       ii. If State has NO Cities -> Input field
                //    b. If State is NOT selected -> Dropdown (disabled)

                const showCityDropdown =
                  hasStates && (!formData.org_state || hasCities);

                return (
                  <>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      City{" "}
                      {showCityDropdown && (
                        <span className="text-danger">*</span>
                      )}
                    </Form.Label>
                    {showCityDropdown ? (
                      <Autocomplete
                        disablePortal
                        options={cities}
                        getOptionLabel={(option) => option.name}
                        loading={isLoadingCities}
                        value={
                          cities.find(
                            (c: any) =>
                              c.name?.toLowerCase() ===
                              formData.org_city?.toLowerCase(),
                          ) || null
                        }
                        onChange={(e, selectedCity) => {
                          setFormData((prev) => ({
                            ...prev,
                            org_city: selectedCity?.name || "",
                          }));
                          setErrors((prev) => ({ ...prev, org_city: "" }));
                        }}
                        ListboxProps={autocompleteDropdownStyles.ListboxProps}
                        renderOption={(props, option) =>
                          autocompleteDropdownStyles.renderOption(
                            props,
                            option,
                            (opt) => opt.name,
                          )
                        }
                        renderInput={(params) => (
                          <BootstrapTextField
                            {...params}
                            placeholder="Select City"
                            id="org_city"
                            name="org_city"
                            size="small"
                            error={!!errors.org_city}
                            helperText={errors.org_city}
                            fullWidth
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                            }}
                          />
                        )}
                        disabled={!formData.org_state}
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        id="org_city"
                        name="org_city"
                        placeholder="Enter City"
                        value={formData.org_city}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            org_city: e.target.value,
                          }));
                          setErrors((prev) => ({ ...prev, org_city: "" }));
                        }}
                        style={commonInputStyle}
                        disabled={!formData.org_country}
                      />
                    )}
                  </>
                );
              })()}
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton
              onClick={handleCancelButton}
              disabled={isSubmitting}
            />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default AddOrganisation;
